import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  SupportedLanguage, 
  supportedLanguages, 
  getLocalizedPath, 
  getInternalRoute,
  routeMappings 
} from '@/i18n/config';

interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  getLocalizedUrl: (internalPath: string) => string;
  t: (key: string, options?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>('en');

  // Extract language from URL path
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const urlLang = pathParts[0] as SupportedLanguage;
    
    if (supportedLanguages.includes(urlLang)) {
      if (urlLang !== currentLanguage) {
        setCurrentLanguage(urlLang);
        i18n.changeLanguage(urlLang);
      }
    } else if (location.pathname === '/') {
      // Root path - redirect to default language based on browser preference
      const browserLang = navigator.language.split('-')[0] as SupportedLanguage;
      const detectedLang = supportedLanguages.includes(browserLang) ? browserLang : 'en';
      navigate(`/${detectedLang}/`, { replace: true });
    }
  }, [location.pathname, i18n, navigate, currentLanguage]);

  const setLanguage = (lang: SupportedLanguage) => {
    if (lang === currentLanguage) return;
    
    const pathParts = location.pathname.split('/').filter(Boolean);
    const currentUrlLang = pathParts[0] as SupportedLanguage;
    
    let newPath: string;
    
    if (supportedLanguages.includes(currentUrlLang)) {
      // Current path has language prefix
      const restOfPath = pathParts.slice(1);
      
      if (restOfPath.length === 0) {
        // Home page
        newPath = `/${lang}/`;
      } else {
        // Find internal route from current localized path
        const localizedRoute = restOfPath[0];
        const internalRoute = getInternalRoute(currentLanguage, localizedRoute);
        const newLocalizedRoute = getLocalizedPath(lang, internalRoute);
        const remainingPath = restOfPath.slice(1).join('/');
        newPath = `/${lang}/${newLocalizedRoute}${remainingPath ? `/${remainingPath}` : ''}`;
      }
    } else {
      // No language prefix - add one
      newPath = `/${lang}${location.pathname}`;
    }
    
    setCurrentLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
    navigate(newPath);
  };

  const getLocalizedUrl = (internalPath: string): string => {
    const cleanPath = internalPath.startsWith('/') ? internalPath.slice(1) : internalPath;
    
    if (!cleanPath) {
      return `/${currentLanguage}/`;
    }
    
    const pathParts = cleanPath.split('/');
    const firstPart = pathParts[0];
    const localizedFirst = getLocalizedPath(currentLanguage, firstPart);
    const restOfPath = pathParts.slice(1).join('/');
    
    return `/${currentLanguage}/${localizedFirst}${restOfPath ? `/${restOfPath}` : ''}`;
  };

  const translateFn = (key: string, options?: Record<string, string | number>): string => {
    return t(key, options) as string;
  };

  return (
    <LanguageContext.Provider value={{ 
      currentLanguage, 
      setLanguage, 
      getLocalizedUrl,
      t: translateFn
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
